import { QueryClient } from '@tanstack/react-query';

/**
 * The one query client the user app has (`CLAUDE.md §6` — server state in TanStack Query, UI state in
 * Zustand, the offline queue in Dexie, and never the three mixed).
 *
 * The defaults are set here rather than per call, because a default set at a call site is a default the
 * next screen forgets.
 *
 * **No retry on a `4xx`.** A `404` is an answer and a `422` is a refusal; retrying either sends the same
 * request to get the same reply, three times, while somebody watches a spinner. A `5xx` and a dropped
 * connection are worth one retry — this is a phone app and Tuesday on a train is not exceptional.
 *
 * **`refetchOnWindowFocus` is off.** A phone "focuses" every time somebody switches back from their
 * banking app, and refetching the ledger each time is a request nobody asked for on a connection they are
 * paying for. Screens that need fresh data after a write invalidate the key instead, which is precise.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          const status = (error as { status?: number } | null)?.status;

          if (typeof status === 'number' && status >= 400 && status < 500) {
            return false;
          }

          return failureCount < 1;
        },

        // Half a minute. Long enough that moving between Home and the ledger does not refetch, short
        // enough that a stale balance is measured in seconds rather than in how long the app was open.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: {
        // A write is never retried automatically. `POST /transactions` is idempotent on the client's id
        // (`FEAT-011 §5.2`), but a `PATCH` is not, and a library that quietly replayed one would be
        // applying an edit twice against a `rowVersion` that had already moved.
        retry: false,
      },
    },
  });
}
